import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Form, Button } from 'react-bootstrap';
import { toastr } from 'Helpers/ToastHelper';
import { SettingsApiService } from 'Services/ApiServices';
import F2WCustomLogger from 'Services/F2WCustomLogger';

const IndividualTemplateModal = ({ showEditModal, setShowEditModal, currentRowData, setRefreshComponent }) => {
  const [modalTemplateValues, setModalTemplateValues] = useState({
    title: '',
    description: '',
    category: '',
    parentId: '',
  });

  const [availableGroups, setAvailableGroups] = useState([]);
  const isNewTemplate = currentRowData.isNew;
  useEffect(() => {
    if (currentRowData && (currentRowData.title || currentRowData.isNew)) {
      setModalTemplateValues({
        title: currentRowData.title || '',
        description: currentRowData.description || '',
        category: currentRowData.category || '',
        parentId: currentRowData.parentId || '',
      });
    }
  }, [currentRowData]);

  // Load available template groups for new templates
  useEffect(() => {
    if (isNewTemplate) {
      SettingsApiService.getAll()
        .then((response) => {
          if (response && response.templates) {
            setAvailableGroups(response.templates);
          }
        })
        .catch((error) => {
          F2WCustomLogger.error('Error loading template groups', error, { modal: 'IndividualTemplate' });
        });
    }
  }, [isNewTemplate]);

  const handleSaveTemplate = () => {
    if (
      !modalTemplateValues.title.trim() ||
      !modalTemplateValues.description.trim() ||
      !modalTemplateValues.category.trim()
    ) {
      toastr.error('Error', 'Please fill in all required fields');
      return;
    }

    if (isNewTemplate && !modalTemplateValues.parentId) {
      toastr.error('Error', 'Please select a template group');
      return;
    }

    const templateData = {
      title: modalTemplateValues.title.trim(),
      description: modalTemplateValues.description.trim(),
      category: modalTemplateValues.category.trim(),
      updatedAt: new Date().toISOString(),
    };

    if (isNewTemplate) {
      templateData.createdAt = new Date().toISOString();
    }
    if (isNewTemplate) {
      // Create new template - add to existing template group
      const selectedGroup = availableGroups.find((g) => g.dropdownName === modalTemplateValues.parentId);
      SettingsApiService.createTemplateOption(selectedGroup?.dropdownName || modalTemplateValues.parentId, templateData)
        .then(() => {
          setShowEditModal(false);
          setRefreshComponent((prev) => !prev);
          toastr.success('Success', 'Template created successfully');
        })
        .catch((error) => {
          F2WCustomLogger.error('Error creating template', error, { modal: 'IndividualTemplate', templateData });
          toastr.error('Error', 'Failed to create template');
        });
    } else {
      // Update existing template
      const templateIndex = currentRowData._id.split('-')[1];
      const parentDropdownName = currentRowData.dropdownName;
      SettingsApiService.updateTemplateOption(parentDropdownName, templateIndex, templateData)
        .then(() => {
          setShowEditModal(false);
          setRefreshComponent((prev) => !prev);
          toastr.success('Success', 'Template updated successfully');
        })
        .catch((error) => {
          F2WCustomLogger.error('Error updating template', error, {
            modal: 'IndividualTemplate',
            templateIndex,
            templateData,
          });
          toastr.error('Error', 'Failed to update template');
        });
    }
  };

  return (
    <Modal
      show={showEditModal && (currentRowData.title || currentRowData.isNew)}
      onHide={() => {
        setModalTemplateValues({ title: '', description: '', category: '', parentId: '' });
        setShowEditModal(false);
      }}
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {isNewTemplate ? 'Create New Template' : `Edit Template: ${modalTemplateValues.title}`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          {isNewTemplate && (
            <Form.Group className="mb-3">
              <Form.Label>Template Group *</Form.Label>
              <Form.Control
                as="select"
                value={modalTemplateValues.parentId}
                onChange={(e) =>
                  setModalTemplateValues({
                    ...modalTemplateValues,
                    parentId: e.target.value,
                  })
                }
              >
                <option value="">Select a template group...</option>
                {availableGroups.map((group) => (
                  <option key={group.dropdownName} value={group.dropdownName}>
                    {group.dropdownName}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Template Title *</Form.Label>
            <Form.Control
              type="text"
              value={modalTemplateValues.title}
              onChange={(e) =>
                setModalTemplateValues({
                  ...modalTemplateValues,
                  title: e.target.value,
                })
              }
              placeholder="Enter template title"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Template Description *</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              value={modalTemplateValues.description}
              onChange={(e) =>
                setModalTemplateValues({
                  ...modalTemplateValues,
                  description: e.target.value,
                })
              }
              placeholder="Enter template description"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Category *</Form.Label>
            <Form.Control
              type="text"
              value={modalTemplateValues.category}
              onChange={(e) =>
                setModalTemplateValues({
                  ...modalTemplateValues,
                  category: e.target.value,
                })
              }
              placeholder="Enter category"
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowEditModal(false)}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSaveTemplate}>
          {isNewTemplate ? 'Create Template' : 'Save Changes'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

IndividualTemplateModal.propTypes = {
  showEditModal: PropTypes.bool.isRequired,
  setShowEditModal: PropTypes.func.isRequired,
  currentRowData: PropTypes.shape({
    _id: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    category: PropTypes.string,
    parentId: PropTypes.string,
    dropdownName: PropTypes.string,
    isNew: PropTypes.bool,
  }).isRequired,
  setRefreshComponent: PropTypes.func.isRequired,
};

export default IndividualTemplateModal;
